/**
 * Popover ancorado no gatilho: fecha com Esc ou clique fora. Prefira isto a
 * modais para ações rápidas no contexto.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export function Popover({
  gatilho,
  children,
  alinhamento = "fim",
  largura = "w-72",
  aberto: abertoControlado,
  onAberto,
  lado = "baixo",
}: {
  gatilho: (p: { aberto: boolean; alternar: () => void }) => ReactNode;
  children: ReactNode | ((fechar: () => void) => ReactNode);
  alinhamento?: "inicio" | "fim";
  largura?: string;
  aberto?: boolean;
  onAberto?: (v: boolean) => void;
  /** "cima" para gatilhos no rodapé (ex.: compositor). */
  lado?: "baixo" | "cima";
}) {
  const [interno, setInterno] = useState(false);
  const aberto = abertoControlado ?? interno;
  const setAberto = onAberto ?? setInterno;
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto, setAberto]);

  const fechar = () => setAberto(false);
  return (
    <div ref={raiz} className="relative">
      {gatilho({ aberto, alternar: () => setAberto(!aberto) })}
      {aberto && (
        <div
          role="dialog"
          className={cn(
            "absolute z-50 max-w-[calc(100vw-24px)] rounded-xl border border-neutral-200 bg-white p-1.5 shadow-lg",
            lado === "cima" ? "bottom-[calc(100%+6px)]" : "top-[calc(100%+6px)]",
            alinhamento === "fim" ? "right-0" : "left-0",
            largura,
          )}
        >
          {typeof children === "function" ? children(fechar) : children}
        </div>
      )}
    </div>
  );
}

export function ItemMenu({
  icone: Icone,
  children,
  onClick,
  perigo,
  disabled,
  descricao,
}: {
  icone?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  children: ReactNode;
  onClick?: () => void;
  perigo?: boolean;
  disabled?: boolean;
  descricao?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-neutral-100 disabled:opacity-50",
        perigo ? "text-danger-500" : "text-neutral-800",
      )}
    >
      {Icone && (
        <span className="mt-0.5 shrink-0 text-neutral-500">
          <Icone size={16} strokeWidth={1.75} />
        </span>
      )}
      <span className="min-w-0">
        <span className="block font-semibold">{children}</span>
        {descricao && <span className="block text-xs text-neutral-500">{descricao}</span>}
      </span>
    </button>
  );
}
